// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Audit Log API Hooks
// apps/web/src/lib/api/audit.ts
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '@forgetrack/api-client';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface AuditLogEntry {
  id: number;
  action: string;
  entityType: string | null;
  entityId: number | null;
  userId: number | null;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
  oldValue: string | null;
  newValue: string | null;
  timestamp: string;
}

export interface AuditLogsPage {
  items: AuditLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface AuditStats {
  totalLogs: number;
  todayCount: number;
  uniqueUsers: number;
  byAction: { action: string; count: number }[];
}

export interface AuditFilters {
  action?: string;
  entityType?: string;
  userId?: number;
}

// ─── Generic fetch ─────────────────────────────────────────────────────────

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: { message: string; status: number } | null;
  refetch: () => Promise<void>;
}

function useApiFetch<T>(path: string, enabled = true): UseApiState<T> {
  const { createApi } = useApiClient();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<{ message: string; status: number } | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const api = await createApi();
      const response = await api.get<T>(path);
      setData(response.data);
    } catch (err: any) {
      setError({ message: err.response?.data?.error || err.message || 'Request failed', status: err.response?.status || 500 });
    } finally {
      setLoading(false);
    }
  }, [createApi, path]);

  useEffect(() => { if (enabled) fetch(); }, [fetch, enabled]);
  return { data, loading, error, refetch: fetch };
}

// ─── Hooks ─────────────────────────────────────────────────────────────────

export function useAuditLogs(page: number, filters: AuditFilters = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', '50');
  if (filters.action) params.set('action', filters.action);
  if (filters.entityType) params.set('entityType', filters.entityType);
  if (filters.userId) params.set('userId', String(filters.userId));

  return useApiFetch<AuditLogsPage>(`/api/v1/audit/logs?${params.toString()}`);
}

export function useAuditStats() {
  return useApiFetch<AuditStats>('/api/v1/audit/stats');
}

export function useAuditActions() {
  return useApiFetch<string[]>('/api/v1/audit/actions');
}
